import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "../../ui/dialog";
import { Drawer, DrawerContent } from "../../ui/drawer";
import { Calendar } from "../../ui/calendar";
import { Trash2, CalendarDays, CreditCard, Car, X } from "lucide-react";
import { Button } from "../../ui/button";
import SpotInfo from "../rest/SpotInfo";
import CostSummary from "../rest/CostSummary";
import useUser from "../../../api/user";
import useParking from "../../../api/parking";
import { POINTS_CONFIG } from "../../../api/mock/userData";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Card } from "../../ui/card";
import { useTranslation } from "react-i18next";

const ReserveSpotModal = ({
  spot,
  isOpen,
  onClose,
  onCancel,
  reservedDates,
  userId,
}) => {
  const { t } = useTranslation();
  const [userReservations, setUserReservations] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const parkingApi = useParking();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchUserReservations = async () => {
      if (!spot || !userId) return;
      try {
        const { reservations } = await parkingApi.getReservations({
          spotId: spot.id,
          userId: userId,
        });
        const spotReservations = reservations.filter(
          (res) =>
            res.parking_spot_id === spot.id &&
            res.user_id === userId &&
            res.status !== "cancelled"
        );
        setUserReservations(spotReservations);
      } catch (error) {
        console.error("Error fetching reservations", error);
      }
    };
    fetchUserReservations();
  }, [spot, userId]);

  const hasReservations = userReservations.length > 0;

  const ReservationCard = ({ reservation }) => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const reservationEndDate = new Date(reservation.end_time);
    reservationEndDate.setHours(23, 59, 59, 999);
    const isActive = reservationEndDate >= currentDate;
    const canCancel = isActive && reservation.status === "active";

    return (
      <Card key={reservation.id} className="p-4 mb-3">
        <div className="flex justify-between items-start mb-2">
          <div className="text-sm font-semibold">
            {format(new Date(reservation.start_time), "dd/MM/yyyy")} -{" "}
            {format(new Date(reservation.end_time), "dd/MM/yyyy")}
            <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
              isActive 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                : 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400'
            }`}>
              {isActive ? 'Active' : 'Ended'}
            </span>
          </div>
          {canCancel && (
            <Button
              variant="destructive"
              size="icon"
              className="h-8 w-8"
              onClick={() => onCancel(reservation.id, reservation.points_cost)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {t("parking.reserve.cost")}: {reservation.points_cost}{" "}
          {t("parking.reserve.points")}
        </div>
      </Card>
    );
  };

  const ModalContent = () => (
    <>
      <div className="bg-primary/5 p-4 md:p-6 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Car className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-lg font-semibold">
                {t("parking.reserve.title")}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                {spot?.spot_number
                  ? t("parking.reserve.booking", {
                      spotNumber: spot.spot_number,
                    })
                  : t("parking.reserve.loading")}
              </p>
            </div>
          </div>
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 p-6">
        <div className="flex-1 space-y-4">
          <SpotInfo spot={spot} />

          {/* Kalender alleen tonen, niet selecteerbaar */}
          <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg border">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="h-4 w-4 text-primary" />
              <h4 className="font-medium">
                {t("parking.reserve.reservations")}
              </h4>
            </div>
            <Calendar
              mode="range"
              // Geen onSelect → dus geen nieuwe reservatie
              disabled={() => true}
              modifiers={{
                reserved: userReservations.map((res) => ({
                  from: new Date(res.start_time),
                  to: new Date(res.end_time),
                })),
              }}
              modifiersStyles={{
                reserved: {
                  backgroundColor: "rgba(147, 51, 234, 0.1)",
                  color: "#9333ea",
                },
              }}
            />
          </div>
        </div>

        {hasReservations && (
          <div className="w-full md:w-80">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-purple-600" />
              {t("parking.reserve.yourReservations")}
            </h3>
            {userReservations.map((res) => (
              <ReservationCard key={res.id} reservation={res} />
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t flex justify-end bg-neutral-50 dark:bg-neutral-900">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[90vh]">
          <div className="flex flex-col h-[85vh]">
            <ModalContent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto w-[95vw] p-0 sm:max-w-[800px]">
        <ModalContent />
      </DialogContent>
    </Dialog>
  );
};

export default ReserveSpotModal;
